import React, { useState, useEffect } from 'react'
import { ArrowLeft, Swords, Zap, Trophy, Shield, Flame } from 'lucide-react'
import { VersusHeader } from '../VersusHeader.jsx'
import { profilePhoto } from '../Avatar.jsx'

const DUEL_QUESTIONS = [
  {
    q: 'Si 3 gatos cazan 3 ratones en 3 minutos, ¿cuántos gatos cazan 100 ratones en 100 minutos?',
    options: ['3 gatos', '100 gatos', '30 gatos', '1 gato'],
    correct: 0,
  },
  {
    q: 'Completa la secuencia: 2, 4, 8, 16, 32, ¿...?',
    options: ['48', '64', '60', '72'],
    correct: 1,
  },
  {
    q: '¿Qué palabra no encaja en el grupo: Axioma, Teorema, Postulado, Metáfora?',
    options: ['Axioma', 'Teorema', 'Metáfora', 'Postulado'],
    correct: 2,
  },
  {
    q: 'Calcula a velocidad de reflejo: 14 × 5 + 30 = ¿...?',
    options: ['90', '100', '110', '95'],
    correct: 1,
  },
  {
    q: '¿Cuál es la falacia en: "Nadie ha demostrado que los aliens no existen, por lo tanto existen"?',
    options: ['Ad Hominem', 'Ad Ignorantiam', 'Hombre de Paja', 'Falsa Dicotomía'],
    correct: 1,
  },
]

export function SynapseBattleGame({ onBack, onComplete, session }) {
  const [index, setIndex] = useState(0)
  const [playerScore, setPlayerScore] = useState(0)
  const [rivalScore, setRivalScore] = useState(0)
  const [done, setDone] = useState(false)
  const [rivalProgress, setRivalProgress] = useState(0)

  const current = DUEL_QUESTIONS[index]

  const me = {
    name: session?.name || session?.email?.split('@')[0] || 'Tú',
    src: session?.picture || profilePhoto('viajero'),
    seed: session?.name,
    ring: '#22d3ee',
  }
  const foe = { name: 'Rival', src: profilePhoto('sinapsis'), seed: 'sinapsis', ring: '#fb7185' }

  // Simulate rival online actions
  useEffect(() => {
    if (done) return
    const rivalTimer = setInterval(() => {
      setRivalProgress((prev) => {
        const next = prev + Math.floor(Math.random() * 8) + 4
        if (next >= 100) {
          setRivalScore((s) => s + 50)
          return 0
        }
        return next
      })
    }, 400)
    return () => clearInterval(rivalTimer)
  }, [index, done])

  const handleAnswer = (optionIndex) => {
    const isCorrect = optionIndex === current.correct
    const added = isCorrect ? 100 : 0
    setPlayerScore((s) => s + added)

    if (index + 1 >= DUEL_QUESTIONS.length) {
      setDone(true)
      onComplete?.(playerScore + added)
    } else {
      setIndex((i) => i + 1)
      setRivalProgress(0)
    }
  }

  if (done) {
    const won = playerScore >= rivalScore
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 text-center space-y-6">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center shadow-xl animate-bounce ${
            won
              ? 'bg-gradient-to-tr from-purple-600 to-amber-500 shadow-purple-500/40'
              : 'bg-slate-800'
          }`}
        >
          <Trophy size={40} className="text-white" />
        </div>

        <div>
          <span className="px-3 py-1 rounded-full bg-purple-950 border border-purple-500/40 text-purple-300 text-xs font-bold uppercase tracking-wider">
            {won ? '¡VICTORIA CÓSMICA!' : 'Duelo Finalizado'}
          </span>
          <h2 className="text-2xl font-black text-white mt-2">
            {won ? '¡Has superado a tu rival!' : '¡Buen intento! Sigue entrenando.'}
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            {won ? 'Cofre Cósmico desbloqueado (+200 BP y +50 Gemas)' : 'Ganas +50 BP por participar'}
          </p>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 w-full max-w-xs space-y-3">
          <div className="flex justify-between items-center text-xs">
            <span className="text-purple-400 font-bold">TÚ (Puntaje)</span>
            <span className="text-white font-black text-sm">{playerScore} BP</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-400 font-medium">Rival Online</span>
            <span className="text-slate-300 font-black">{rivalScore} BP</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="w-full max-w-xs py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-extrabold text-sm shadow-lg active:scale-95 transition-all cursor-pointer"
        >
          Volver a Duelos
        </button>
      </div>
    )
  }

  return (
    <div className="p-4 flex flex-col min-h-[80vh] max-w-md mx-auto">
      {/* Top Match Bar */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-300 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-950 border border-purple-500/40 text-purple-300 text-xs font-bold">
          <Swords size={14} className="text-purple-400" />
          <span>DUELO 1V1 EN VIVO</span>
        </div>

        <div className="text-xs font-bold text-cyan-400">{playerScore} BP</div>
      </div>

      {/* Duel Arena — face-off */}
      <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 mb-4 space-y-3">
        <VersusHeader
          me={{ ...me, sub: `${playerScore} BP` }}
          foe={{ ...foe, sub: `${rivalScore} BP` }}
          size={54}
        />
        <div className="space-y-2">
          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-cyan-400 rounded-full transition-all"
              style={{ width: `${((index + 1) / DUEL_QUESTIONS.length) * 100}%` }}
            />
          </div>
          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-rose-500 rounded-full transition-all duration-300"
              style={{ width: `${rivalProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 flex flex-col justify-center my-4">
        <div className="p-5 rounded-2xl bg-gradient-to-b from-[#181226] to-[#0f0b18] border border-purple-800/40 text-center shadow-xl">
          <span className="text-[11px] font-bold text-purple-400 uppercase tracking-wider">
            Acertijo {index + 1} de {DUEL_QUESTIONS.length}
          </span>
          <p className="text-base font-bold text-white mt-2 leading-relaxed">
            {current.q}
          </p>
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        {current.options.map((opt, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => handleAnswer(idx)}
            className="py-3.5 px-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-purple-500 hover:bg-purple-950/40 text-sm font-bold text-white active:scale-95 transition-all cursor-pointer text-center"
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}
